/*
 * Quick buy (mobile bottom sheet).
 *
 * Each product card carries an inert <template data-quickbuy-template> with the
 * full quick-buy form (snippets/quick-buy.liquid): the PDP-style swatch / size
 * selector, a hidden <select data-qb-variant-id> whose <option>s hold the variant
 * + anti-slip underlay data, the underlay add-on card, and an add-to-basket button.
 *
 * Tapping a card's [data-quickbuy-open] clones that template into a single shared
 * bottom-sheet drawer and wires it up. Variant changes update the price, the
 * add button and the underlay exactly like the PDP, reusing window.UnderlayCart.
 */
(function () {
  let drawer = null; // { overlay, panel, content } shared shell
  let lockedScroll = false;

  function buildDrawer() {
    const overlay = document.createElement('div');
    overlay.setAttribute('data-qb-overlay', '');
    overlay.className =
      'invisible fixed inset-0 z-40 bg-black/40 opacity-0 transition-opacity duration-200 data-active:visible data-active:opacity-100 lg:hidden';

    const panel = document.createElement('div');
    panel.setAttribute('data-qb-panel', '');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.className =
      'invisible fixed inset-x-0 bottom-0 z-50 flex max-h-[88vh] translate-y-full flex-col rounded-t-[16px] bg-(--color-bg) text-(--color-fg) shadow-2xl transition-transform duration-300 ease-out data-active:visible data-active:translate-y-0 lg:hidden';

    const head = document.createElement('div');
    head.className = 'relative flex shrink-0 items-center justify-center px-4 pt-3 pb-1';
    head.innerHTML =
      '<span class="block h-1 w-10 rounded-full bg-(--color-fg)/20" aria-hidden="true"></span>' +
      '<button type="button" data-qb-close aria-label="Close" class="absolute right-3 top-2 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-(--color-fg) transition hover:bg-(--color-fg)/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-accent)">' +
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
      '</button>';

    const content = document.createElement('div');
    content.setAttribute('data-qb-content', '');
    content.className = 'min-h-0 flex-1 overflow-y-auto px-4 pb-[max(24px,env(safe-area-inset-bottom))] pt-1';

    panel.appendChild(head);
    panel.appendChild(content);
    document.body.appendChild(overlay);
    document.body.appendChild(panel);

    overlay.addEventListener('click', close);
    head.querySelector('[data-qb-close]').addEventListener('click', close);

    drawer = { overlay, panel, content };
    return drawer;
  }

  function open() {
    const d = drawer || buildDrawer();
    // Force a reflow so the transition runs from the translated state.
    void d.panel.offsetHeight;
    d.overlay.setAttribute('data-active', '');
    d.panel.setAttribute('data-active', '');
    document.body.style.overflow = 'hidden';
    lockedScroll = true;
  }

  function close() {
    if (!drawer) return;
    drawer.overlay.removeAttribute('data-active');
    drawer.panel.removeAttribute('data-active');
    if (lockedScroll) {
      document.body.style.overflow = '';
      lockedScroll = false;
    }
    // Clear the cloned form once the slide-out finishes.
    window.setTimeout(() => {
      if (drawer && !drawer.panel.hasAttribute('data-active')) drawer.content.innerHTML = '';
    }, 320);
  }

  // ----- Wire a freshly-cloned quick-buy form (mirrors the PDP product form) -----
  function initForm(form) {
    const optionInputs = form.querySelectorAll('[data-qb-option]');
    const variantSelect = form.querySelector('[data-qb-variant-id]');
    const submit = form.querySelector('[data-qb-submit]');
    const submitLabel = form.querySelector('[data-qb-submit-label]');
    const priceEl = form.querySelector('[data-qb-price]');

    const strings = {
      addToCart: form.dataset.stringAddToCart || 'Add to basket',
      soldOut: form.dataset.stringSoldOut || 'Sold out',
      unavailable: form.dataset.stringUnavailable || 'Unavailable',
    };

    // ----- Anti-slip underlay (deferred - committed with the product) -----
    const antislipCard = form.querySelector('[data-qb-antislip-card]');
    const antislipButton = antislipCard && antislipCard.querySelector('[data-qb-antislip-add]');
    const antislipAddedGroup = antislipCard && antislipCard.querySelector('[data-qb-antislip-added]');
    const antislipRemove = antislipCard && antislipCard.querySelector('[data-qb-antislip-remove]');
    const antislipImage = antislipCard && antislipCard.querySelector('[data-qb-antislip-image]');
    const antislipPrice = antislipCard && antislipCard.querySelector('[data-qb-antislip-price]');
    const antislipNote = form.querySelector('[data-qb-antislip-note]');

    // Not part of the underlay: the "Outlet" discount notice or the urgency message,
    // resolved per variant in Liquid onto each <option> (mirrors the PDP).
    const updateUrgencyNoteForVariant = (matched) => {
      if (!antislipNote) return;
      const message = matched.dataset.urgencyMessage || '';
      antislipNote.textContent = message;
      antislipNote.hidden = !message;
    };
    // Selected = the customer wants the underlay added with the product.
    let antislipSelected = antislipCard ? !!antislipCard.dataset.underlayKey : false;

    // Toggles between the "Add" pill and the "Remove" group (matches the cart card).
    const setAntislipAdded = (isAdded) => {
      const flag = isAdded ? 'true' : 'false';
      if (antislipButton) antislipButton.dataset.added = flag;
      if (antislipAddedGroup) antislipAddedGroup.dataset.added = flag;
      if (antislipCard) antislipCard.dataset.added = flag;
    };
    setAntislipAdded(antislipSelected);

    function updateUnderlayForVariant(matched) {
      if (!antislipCard) return;
      const id = matched.dataset.underlayId || '';
      antislipCard.dataset.parentVariantId = matched.value;
      antislipCard.hidden = !id;

      if (id) {
        if (antislipButton) {
          antislipButton.dataset.variantId = id;
          antislipButton.disabled = matched.dataset.underlayAvailable === 'false';
        }
        if (antislipImage) {
          const img = matched.dataset.underlayImage || '';
          if (img) { antislipImage.src = img; antislipImage.hidden = false; }
          else { antislipImage.removeAttribute('src'); antislipImage.hidden = true; }
        }
        if (antislipPrice && matched.dataset.underlayPrice) antislipPrice.textContent = matched.dataset.underlayPrice;
      }
      // Deferred: variant change keeps the selection + display in sync only.
      setAntislipAdded(antislipSelected);
    }

    if (antislipButton) {
      antislipButton.addEventListener('click', () => {
        // Select only - nothing is added until the product is added.
        antislipSelected = true;
        setAntislipAdded(true);
      });
    }

    if (antislipRemove) {
      antislipRemove.addEventListener('click', () => {
        // Deselect so the underlay isn't committed when the product is added.
        antislipSelected = false;
        setAntislipAdded(false);
      });
    }

    // Commit the underlay to the cart, linked to the just-added parent variant.
    async function commitUnderlay(parentVariantId) {
      if (!antislipCard || !window.UnderlayCart || !parentVariantId) return;
      const underlayId = antislipButton && antislipButton.dataset.variantId;
      let cart;
      try { cart = await window.UnderlayCart.getCart(); } catch (e) { return; }
      const existing = (cart.items || []).find(
        (it) => it.properties && String(it.properties._underlay_for) === String(parentVariantId)
      );
      try {
        if (antislipSelected && underlayId && antislipButton && !antislipButton.disabled && !existing) {
          await window.UnderlayCart.addUnderlay(underlayId, parentVariantId, 1);
          await window.UnderlayCart.reconcile();
        } else if (!antislipSelected && existing) {
          await window.UnderlayCart.updateLines({ [existing.key]: 0 });
        }
      } catch (e) {
        // Non-fatal - the product is already in the cart.
      }
    }

    // ----- Variant selection -----
    if (variantSelect) {
      const selectedOptions = () => {
        const byIndex = {};
        // Re-queried per call rather than using the list captured at mount: a
        // swapped-in selector renders new inputs that the old list never saw.
        form.querySelectorAll('[data-qb-option]').forEach((input) => {
          if (input.checked) byIndex[input.dataset.optionIndex] = input.value;
        });
        const out = [];
        for (let i = 0; byIndex[i] !== undefined; i++) out.push(byIndex[i]);
        return out;
      };

      const update = () => {
        const target = selectedOptions().join(' / ');
        let matched = null;
        variantSelect.querySelectorAll('option').forEach((opt) => {
          if (opt.dataset.variantOptions === target) matched = opt;
        });

        if (!matched) {
          if (submit) submit.disabled = true;
          if (submitLabel) submitLabel.textContent = strings.unavailable;
          return;
        }

        variantSelect.value = matched.value;
        const available = matched.dataset.variantAvailable === 'true';
        if (submit) submit.disabled = !available;
        if (submitLabel) submitLabel.textContent = available ? strings.addToCart : strings.soldOut;

        if (priceEl) {
          const price = matched.dataset.variantPrice;
          const compare = matched.dataset.variantCompareAtPrice;
          priceEl.innerHTML = compare
            ? '<span class="text-(--color-fg)">' + price + '</span> <span class="text-(--color-muted) line-through">' + compare + '</span>'
            : '<span class="text-(--color-fg)">' + price + '</span>';
        }
        updateUnderlayForVariant(matched);
        updateUrgencyNoteForVariant(matched);
      };

      // Delegated on the form: `change` bubbles, so one listener covers every
      // option input the sheet holds now or after the markup inside is replaced.
      // Binding per input broke as soon as an A/B variant changed the card
      // template these are cloned from.
      form.addEventListener('change', (event) => {
        if (event.target && event.target.matches && event.target.matches('[data-qb-option]')) update();
      });
    }

    // ----- Add to basket -----
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const id = variantSelect ? variantSelect.value : (form.querySelector('input[name="id"]') || {}).value;
      if (!id) return;
      submit.disabled = true;
      const original = submitLabel.textContent;
      submitLabel.textContent = 'Adding…';
      try {
        // Market-prefixed: a bare '/cart/add.js' resolves in the default market.
        const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
        // The product is in. The underlay is its own request: if that fails,
        // saying "couldn't add - try again" would invite a second rug, so
        // close and show the basket - what went in is what they see.
        try {
          await commitUnderlay(String(id));
        } catch (underlayErr) {
          console.warn('[quick-buy] added, underlay could not be added', underlayErr);
        }
        close();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      } catch (err) {
        submitLabel.textContent = 'Couldn’t add - try again';
        window.setTimeout(() => { submitLabel.textContent = original; }, 2000);
      } finally {
        submit.disabled = false;
      }
    });
  }

  function mountForm(node) {
    const d = drawer || buildDrawer();
    d.content.innerHTML = '';
    d.content.appendChild(node);
    const form = d.content.querySelector('[data-qb-form]');
    if (form) initForm(form);
  }

  function message(text) {
    return '<div class="flex items-center justify-center py-16 text-center font-sans text-[14px] text-(--color-muted)">' + text + '</div>';
  }

  // Cards rendered in JS (search results, "People love…") have no inline template,
  // so fetch the bare quick-buy fragment via the ?view=quickbuy product template.
  async function fetchForm(url) {
    const d = drawer || buildDrawer();
    d.content.innerHTML = message('Loading…');
    open();
    try {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      const res = await fetch(url + sep + 'view=quickbuy', { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('quick-buy fetch failed');
      const tmp = document.createElement('div');
      tmp.innerHTML = await res.text();
      const fragment = document.createDocumentFragment();
      while (tmp.firstChild) fragment.appendChild(tmp.firstChild);
      if (fragment.querySelector('[data-qb-form]')) mountForm(fragment);
      else d.content.innerHTML = message('Couldn’t load this product.');
    } catch (err) {
      d.content.innerHTML = message('Couldn’t load this product.');
    }
  }

  // ----- Open: inline template (fast path) or fetched fragment -----
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-quickbuy-open]');
    if (!opener) return;
    // Mobile only - on desktop let the Buy link navigate to the product page.
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    const root = opener.closest('[data-quickbuy-root]');
    const template = root && root.querySelector('template[data-quickbuy-template]');
    const url = opener.dataset.quickbuyUrl || (root && root.dataset.quickbuyUrl);
    if (!template && !url) return;
    e.preventDefault();

    if (template) {
      mountForm(template.content.cloneNode(true));
      open();
    } else {
      fetchForm(url);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer && drawer.panel.hasAttribute('data-active')) close();
  });
})();
